import React, { useEffect, Suspense, lazy } from 'react';
import { HashRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { initFirebase } from './firebase';
import Navbar from './components/Layout/Navbar';
import Footer from './components/Layout/Footer';
import ScrollToTop from './components/ui/ScrollToTop';
import CookieBanner from './components/ui/CookieBanner';

const Home = lazy(() => import('./pages/Home'));
const Services = lazy(() => import('./pages/Services'));
const Solutions = lazy(() => import('./pages/Solutions'));
const SolutionDetail = lazy(() => import('./pages/SolutionDetail'));
const Technology = lazy(() => import('./pages/Technology'));
const About = lazy(() => import('./pages/About'));
const Careers = lazy(() => import('./pages/Careers'));
const Insights = lazy(() => import('./pages/Insights'));
const InsightDetail = lazy(() => import('./pages/InsightDetail'));
const Contact = lazy(() => import('./pages/Contact'));
const Legal = lazy(() => import('./pages/Legal'));
const ServiceDetail = lazy(() => import('./pages/ServiceDetail'));

const SITE_TITLE = 'Infraverge';

const PAGE_TITLES: Record<string, string> = {
  '/': `${SITE_TITLE} | AI, Data and Cloud Infrastructure`,
  '/services': `Services | ${SITE_TITLE}`,
  '/solutions': `Solutions | ${SITE_TITLE}`,
  '/technology': `Technology | ${SITE_TITLE}`,
  '/about': `About | ${SITE_TITLE}`,
  '/careers': `Careers | ${SITE_TITLE}`,
  '/insights': `Insights | ${SITE_TITLE}`,
  '/contact': `Contact | ${SITE_TITLE}`,
  '/privacy': `Privacy Policy | ${SITE_TITLE}`,
  '/terms': `Terms & Conditions | ${SITE_TITLE}`,
  '/imprint': `Imprint | ${SITE_TITLE}`,
};

function DocumentTitle(): null {
  const location = useLocation();
  const pathname = location.pathname || '/';
  const title = PAGE_TITLES[pathname] ?? `${SITE_TITLE}`;
  useEffect(() => {
    document.title = title;
  }, [title]);
  return null;
}

const App: React.FC = () => {
  useEffect(() => {
    initFirebase();
  }, []);

  return (
    <Router>
      <DocumentTitle />
      <ScrollToTop />
      <div className="flex flex-col min-h-screen bg-slate-50 overflow-x-hidden">
        <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[60] focus:px-4 focus:py-2 focus:bg-blue-600 focus:text-white focus:rounded-lg focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-blue-600">
          Skip to main content
        </a>
        <Navbar />
        <main className="flex-grow w-full min-w-0" id="main-content" role="main" tabIndex={-1}>
          <Suspense fallback={<div className="min-h-[calc(100vh-5rem-18rem)] flex items-center justify-center text-slate-600" aria-live="polite">Loading…</div>}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/services" element={<Services />} />
            <Route path="/services/:slug" element={<ServiceDetail />} />
            <Route path="/solutions" element={<Solutions />} />
            <Route path="/solutions/:slug" element={<SolutionDetail />} />
            <Route path="/technology" element={<Technology />} />
            <Route path="/about" element={<About />} />
            <Route path="/careers" element={<Careers />} />
            <Route path="/insights" element={<Insights />} />
            <Route path="/insights/:slug" element={<InsightDetail />} />
            <Route path="/contact" element={<Contact />} />
            
            {/* Legal Routes */}
            <Route path="/privacy" element={<Legal type="privacy" />} />
            <Route path="/terms" element={<Legal type="terms" />} />
            <Route path="/imprint" element={<Legal type="imprint" />} />
            
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </Suspense>
        </main>
        <Footer />
        <CookieBanner />
      </div>
    </Router>
  );
};

export default App;